import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, ButtonBase, TextField } from '@mui/material';
import type { FieldBaseProps } from './types';

export interface FieldColorProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** Quick-pick hex swatches rendered under the input. */
  swatches?: string[];
  placeholder?: string;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * RHF-bound hex color field: a native color well + the hex text input + an
 * optional swatch row (the prototype `FieldColor`). The stored value is the
 * 7-char hex string; a null/empty value renders an empty input (inheritance —
 * the caller supplies the inherited color via `placeholder`).
 */
export function FieldColor<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  required,
  disabled,
  swatches,
  placeholder,
}: FieldColorProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value: string = field.value ?? '';
        const wellValue = HEX_RE.test(value)
          ? value
          : placeholder && HEX_RE.test(placeholder)
            ? placeholder
            : '#888888';
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box
                component="input"
                type="color"
                aria-label={label}
                disabled={disabled}
                value={wellValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.value)
                }
                sx={{
                  width: 40,
                  height: 40,
                  p: 0.25,
                  mt: '0px',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '8px',
                  bgcolor: 'background.paper',
                  cursor: disabled ? 'default' : 'pointer',
                  flexShrink: 0,
                }}
              />
              <TextField
                {...field}
                value={value}
                size="small"
                label={label}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? helperText}
                slotProps={{
                  htmlInput: {
                    spellCheck: false,
                    style: { fontFamily: 'ui-monospace, Menlo, monospace' },
                  },
                }}
              />
            </Box>
            {swatches && swatches.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, ml: '48px' }}>
                {swatches.map((hex) => (
                  <ButtonBase
                    key={hex}
                    aria-label={hex}
                    disabled={disabled}
                    onClick={() => field.onChange(hex)}
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '6px',
                      bgcolor: hex,
                      border: 1,
                      borderColor: value === hex ? 'text.primary' : 'divider',
                    }}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        );
      }}
    />
  );
}
